import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult } from '@synergy/api';
import { Dispatch } from 'redux';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../../../../../api';
import { AppState } from '../../../../../../store/models/app-state';
import { SupplierDocument } from '../models/supplier-detail-document';

export const SUPPLIER_DETAIL_DOCUMENTS_FETCH = 'SUPPLIER_DETAIL_DOCUMENTS_FETCH';
export const SUPPLIER_DETAIL_DOCUMENTS_RECEIVED = 'SUPPLIER_DETAIL_DOCUMENTS_RECEIVED';
export const SUPPLIER_DETAIL_DOCUMENTS_RENAME = 'SUPPLIER_DETAIL_DOCUMENTS_RENAME';
export const SUPPLIER_DETAIL_DOCUMENTS_SET_SHOP_VISIBILITY = 'SUPPLIER_DETAIL_DOCUMENTS_SET_SHOP_VISIBILITY';
export const SUPPLIER_DETAIL_DOCUMENTS_SET_SUPPLIER_DIRECTORY_VISIBILITY =
    'SUPPLIER_DETAIL_DOCUMENTS_SET_SUPPLIER_DIRECTORY_VISIBILITY';
export const SUPPLIER_DETAIL_DOCUMENTS_DELETE = 'SUPPLIER_DETAIL_DOCUMENTS_DELETE';

export const SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_START = 'SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_START';
export const SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_PROGRESS = 'SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_PROGRESS';
export const SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_SUCCESS = 'SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_SUCCESS';
export const SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_FAILURE = 'SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_FAIL';
export const SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_CANCEL = 'SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_CANCEL';

const receiveDocuments = (supplierDocuments: SupplierDocument[]) => ({
    type: SUPPLIER_DETAIL_DOCUMENTS_RECEIVED,
    supplierDocuments,
});

const uploadDocumentStart = (document: SupplierDocument) => {
    return {
        type: SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_START,
        document,
    };
};

const uploadDocumentProgress = (id: number, progress: number) => {
    return {
        type: SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_PROGRESS,
        id,
        progress,
    };
};

const uploadDocumentCancel = (document: SupplierDocument) => {
    return {
        type: SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_CANCEL,
        document,
    };
};

const uploadDocumentError = (document: SupplierDocument) => {
    return {
        type: SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_FAILURE,
        document,
    };
};

const uploadDocumentSuccess = (id: number, document: SupplierDocument) => {
    return {
        type: SUPPLIER_DETAIL_DOCUMENTS_UPLOAD_SUCCESS,
        id,
        document,
    };
};

export const fetchSupplierDocuments = (id: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_DETAIL_DOCUMENTS_FETCH });

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Documents.getDocuments(id);
        if (response.success) {
            dispatch(receiveDocuments(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

interface UploadInfo {
    file: File;
    supplierDocument: SupplierDocument;
    response?: ApiResult<SupplierDocument>;
}

export const addSupplierDocuments = (files: File[], titles: string[] = []) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        if (!supplierId) {
            return;
        }

        const idBase = new Date().getTime() * -1;
        const uploadInfos = files.map<UploadInfo>((file, index) => ({
            file,
            supplierDocument: {
                id: idBase - index,
                size: file.size,
                title: file.name,
                filesystem: '',
                uploadProgress: 0,
                isVisibleOnProductPages: false,
                isVisibleInSupplierDirectory: false,
            },
        }));
        uploadInfos.forEach(info => dispatch(uploadDocumentStart(info.supplierDocument)));

        const uploadingDocumentPromises = uploadInfos.map(info =>
            api.contactsAndOrganizations.Supplier.SupplierDetail.Documents.addDocument(
                supplierId,
                info.file,
                c => (info.supplierDocument.cancel = c),
                progress => {
                    const percentCompleted = Math.floor((progress.loaded * 100) / progress.total);
                    dispatch(uploadDocumentProgress(info.supplierDocument.id, percentCompleted));
                }
            )
        );

        const responses = await Promise.all(uploadingDocumentPromises);
        responses.forEach((response, index) => (uploadInfos[index].response = response));

        // The same notice the documents give: stored the moment it is dropped (R-197).
        const saved = uploadInfos.filter(info => info.response?.success).length;
        if (saved > 0) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.upload.saved', { count: saved })));
        }

        uploadInfos.forEach((uploadInfo, index) => {
            if (uploadInfo.response?.success) {
                const document = uploadInfo.response.result;
                dispatch(uploadDocumentSuccess(uploadInfo.supplierDocument.id, document));

                // The upload takes the file only; a title given with it follows.
                const title = titles[index]?.trim();
                if (title && title !== document.title) {
                    renameDocument(document.id, title)(dispatch, getState, { api });
                }
            } else {
                if (uploadInfo.response?.error instanceof Error) {
                    dispatch(uploadDocumentError(uploadInfo.supplierDocument));
                } else {
                    dispatch(uploadDocumentCancel(uploadInfo.supplierDocument));
                }
            }
        });
    };
};

export const renameDocument = (id: number, title: string) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        const supplierDocument = getState().contactsAndOrganizations.suppliers.supplierDetail.documents.documents.find(
            document => document.id === id
        );
        if (!supplierId || !supplierDocument) {
            return;
        }

        dispatch({ type: SUPPLIER_DETAIL_DOCUMENTS_RENAME, id, title });

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Documents.updateDocument(
            supplierId,
            { ...supplierDocument, title }
        );

        if (response.success) {
            dispatch(
                Snackbar.enqueue(PoolNotification.success('app.contactsAndOrganizations.documents.editTitleSuccess'))
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const setShopVisibility = (id: number, isVisibleOnProductPages: boolean) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        const supplierDocument = getState().contactsAndOrganizations.suppliers.supplierDetail.documents.documents.find(
            document => document.id === id
        );
        if (!supplierId || !supplierDocument) {
            return;
        }

        dispatch({
            type: SUPPLIER_DETAIL_DOCUMENTS_SET_SHOP_VISIBILITY,
            id,
            isVisibleOnProductPages,
        });

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Documents.updateDocument(
            supplierId,
            { ...supplierDocument, isVisibleOnProductPages }
        );
        if (response.success) {
            if (response.result.isVisibleOnProductPages) {
                dispatch(
                    Snackbar.enqueue(
                        PoolNotification.success('app.contactsAndOrganizations.documents.editShopVisibilityShow')
                    )
                );
            } else {
                dispatch(
                    Snackbar.enqueue(
                        PoolNotification.success('app.contactsAndOrganizations.documents.editShopVisibilityHide')
                    )
                );
            }
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const setSupplierDirectoryVisibility = (id: number, isVisibleInSupplierDirectory: boolean) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        const supplierDocument = getState().contactsAndOrganizations.suppliers.supplierDetail.documents.documents.find(
            document => document.id === id
        );
        if (!supplierId || !supplierDocument) {
            return;
        }

        dispatch({
            type: SUPPLIER_DETAIL_DOCUMENTS_SET_SUPPLIER_DIRECTORY_VISIBILITY,
            id,
            isVisibleInSupplierDirectory,
        });

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Documents.updateDocument(
            supplierId,
            { ...supplierDocument, isVisibleInSupplierDirectory }
        );
        if (response.success) {
            if (response.result.isVisibleInSupplierDirectory) {
                dispatch(
                    Snackbar.enqueue(
                        PoolNotification.success(
                            'app.contactsAndOrganizations.documents.editSupplierDirectoryVisibilityShow'
                        )
                    )
                );
            } else {
                dispatch(
                    Snackbar.enqueue(
                        PoolNotification.success(
                            'app.contactsAndOrganizations.documents.editSupplierDirectoryVisibilityHide'
                        )
                    )
                );
            }
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const deleteDocument = (id: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        if (!supplierId) {
            return;
        }

        dispatch({ type: SUPPLIER_DETAIL_DOCUMENTS_DELETE, id });

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Documents.deleteDocument(
            supplierId,
            id
        );
        if (response.success) {
            dispatch(
                Snackbar.enqueue(PoolNotification.success('app.contactsAndOrganizations.documents.deleteSuccess'))
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
